import OnboardingPageTitle from "./OnboardingPageTitle";
/* eslint-disable react-hooks/exhaustive-deps -- lookup hydrators intentionally follow selected IDs */
import i18n from "../../i18n";
import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./positionHistory.css";
import { resolveApiUrl, resolveOrgApiUrl } from "../../api/api";
import { getAuthenticatedTenantId } from "../../lib/employee";
import sessionDB from "../../utils/sessionDB";
import {
  buildOnboardingAccessMessage,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError,
  resolveOnboardingActionConfig
} from "./onboardingFeatureAccess";
import {
  FormSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";

const ADD_ENDPOINT = "/saas/hrms/position-history/add";
const ADD_SERVICE_CODE = "HR03A";
const UPDATE_SERVICE_CODE = "HR03U";
const SEARCH_SERVICE_CODE = "HR03S";

const ROLES_ENDPOINT = "/saas/hrms/org/roles";
const ROLE_BY_ID_ENDPOINT = "/saas/hrms/org/roles";
const DESIGNATIONS_ENDPOINT = "/saas/hrms/org/designations";
const DESIGNATION_BY_ID_ENDPOINT = "/saas/hrms/org/designations";
const DEPARTMENTS_ENDPOINT = "/saas/hrms/org/departments";
const DEPARTMENT_BY_ID_ENDPOINT = "/saas/hrms/org/departments";
const GRADE_LEVELS_ENDPOINT = "/saas/hrms/org/grade-levels";
const GRADE_LEVEL_BY_ID_ENDPOINT = "/saas/hrms/org/grade-levels";
const DIVISIONS_ENDPOINT = "/saas/hrms/org/divisions";
const DIVISION_BY_ID_ENDPOINT = "/saas/hrms/org/divisions";
const COST_CENTERS_ENDPOINT = "/saas/hrms/org/cost-centers";
const COST_CENTER_BY_ID_ENDPOINT = "/saas/hrms/org/cost-centers";
const WORK_LOCATIONS_ENDPOINT = "/saas/hrms/org/work-locations";
const WORK_LOCATION_BY_ID_ENDPOINT = "/saas/hrms/org/work-locations";
const EMP_TYPES_ENDPOINT = "/saas/hrms/org/emp-type";
const EMPLOYEE_BY_CANONICAL_ROLE_ENDPOINT = "/saas/hrms/current-employment/search/canonicalRoleName";
const EMPLOYEE_BY_ROLE_PAGE_SIZE = 10;

/** Fallback only when /org/emp-type fails — same set Current Employment uses. */
const EMP_TYPES = ["Probationary", "Permanent", "Contract", "Intern", "Consultant", "Notice_Period"];

const EMP_TYPE_OPTION_KEYS = {
  Permanent: "onboarding.options.empType.permanent",
  Probationary: "onboarding.options.empType.probationary",
  Contract: "onboarding.options.empType.contract",
  Intern: "onboarding.options.empType.intern",
  Consultant: "onboarding.options.empType.consultant",
  Notice_Period: "onboarding.options.empType.noticePeriod"
};

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  effectiveDate: "dateTime.effectiveDate",
  empType: "onboarding.employeeType",
  roleId: "onboarding.roleId",
  designationId: "onboarding.designationId",
  departmentId: "onboarding.departmentId",
  gradeLevelId: "onboarding.gradeLevelId",
  divisionId: "onboarding.divisionId",
  costCenterId: "onboarding.costCenterId",
  workLocationId: "onboarding.workLocationId",
  managerId: "onboarding.managerId"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function translateEmpTypeLabel(value) {
  const key = EMP_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateEmpTypeOptions(options) {
  return options.map((option) => ({
    ...option,
    label: translateEmpTypeLabel(option.value)
  }));
}

function normalizeEnumOptions(payload, valueKeys = [], labelKeys = []) {
  const candidates = [
    payload,
    payload?.data,
    payload?.content,
    payload?.result,
    payload?.data?.content,
    payload?.records,
    payload?.items,
    payload?.rows,
    payload?.data?.records
  ];
  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (typeof item === "string") {
        const str = item.trim();
        return str ? { value: str, label: str } : null;
      }
      if (!item || typeof item !== "object") return null;
      const value = [
        ...valueKeys.map((key) => item?.[key]),
        item?.name,
        item?.code,
        item?.value,
        item?.id
      ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
      if (value === null || value === undefined || !String(value).trim()) return null;
      const label = [
        ...labelKeys.map((key) => item?.[key]),
        item?.description,
        item?.label,
        item?.name,
        value
      ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
      return { value: String(value).trim(), label: String(label ?? value).trim() };
    })
    .filter(Boolean);
}

const INITIAL_FORM = {
  empId: "",
  effectiveDate: "",
  empType: "",
  roleId: "",
  designationId: "",
  departmentId: "",
  gradeLevelId: "",
  divisionId: "",
  costCenterId: "",
  workLocationId: "",
  managerId: "",
  managerName: ""
};

/** Convert API / form dates into yyyy-MM-dd for <input type="date">. */
function toInputDate(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return "";
  let str = trimmed.includes("T") ? trimmed.split("T")[0] : trimmed;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split("/");
    return `${year}-${month}-${day}`;
  }
  return "";
}

/** Backend update path expects effectiveDate as dd-MM-yyyy. */
function toApiEffectiveDate(value) {
  const iso = toInputDate(value);
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${day}-${month}-${year}`;
}

function isValidIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function isNumericValue(value) {
  return /^\d+$/.test(String(value || "").trim());
}

/* eslint-disable-next-line no-unused-vars -- retained for API error normalization */
function normalizePositionHistoryErrorMessage(message) {
  return String(message || "").trim() || i18n.t("onboarding.errors.positionHistoryRequestFailed");
}

function normalizeLookupOptions(payload, idKeys = [], labelKeys = []) {
  const candidates = [
    payload,
    payload?.data,
    payload?.content,
    payload?.result,
    payload?.data?.content,
    payload?.result?.content,
    payload?.data?.result,
    payload?.data?.result?.content,
    payload?.records,
    payload?.items,
    payload?.rows,
    payload?.roles,
    payload?.designations,
    payload?.departments,
    payload?.gradeLevels,
    payload?.divisions,
    payload?.costCenters,
    payload?.workLocations,
    payload?.data?.roles,
    payload?.data?.designations,
    payload?.data?.departments,
    payload?.data?.gradeLevels,
    payload?.data?.divisions,
    payload?.data?.costCenters,
    payload?.data?.workLocations,
    payload?.data?.records,
    payload?.data?.items,
    payload?.data?.rows
  ];

  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = [
        ...idKeys.map((key) => item?.[key]),
        item?.role_id,
        item?.designation_id,
        item?.department_id,
        item?.grade_level_id,
        item?.division_id,
        item?.cost_center_id,
        item?.work_location_id,
        item?.location_id
      ].find((value) => value !== null && value !== undefined && String(value).trim());
      const label = [
        ...labelKeys.map((key) => item?.[key]),
        item?.role_name,
        item?.designation_name,
        item?.department_name,
        item?.grade_level_name,
        item?.division_name,
        item?.cost_center_name,
        item?.work_location_name,
        item?.location_name,
        item?.description,
        item?.title
      ].find((value) => value !== null && value !== undefined && String(value).trim());
      if (!id || !label) return null;
      return { value: String(id).trim(), label: String(label).trim() };
    })
    .filter(Boolean);
}

function extractLookupLabel(payload, labelKeys = []) {
  if (!payload || typeof payload !== "object") return "";
  const source = Array.isArray(payload) ? payload[0] : payload;
  const value = [
    ...labelKeys.map((key) => source?.[key]),
    source?.role_name,
    source?.designation_name,
    source?.department_name,
    source?.grade_level_name,
    source?.division_name,
    source?.cost_center_name,
    source?.work_location_name,
    source?.location_name,
    source?.description,
    source?.title
  ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
  return value ? String(value).trim() : "";
}

function unwrapEmployeeRolePage(payload) {
  const root = payload?.data ?? payload ?? {};
  const candidates = [
    root?.content,
    root?.data?.content,
    root?.result?.content,
    root?.records,
    root?.items,
    root?.employees,
    Array.isArray(root?.data) ? root.data : null,
    Array.isArray(root) ? root : null,
    Array.isArray(payload) ? payload : null
  ];
  const content = candidates.find((entry) => Array.isArray(entry)) || [];
  const pageNumber = Number(
    root?.number ?? root?.page ?? root?.pageNumber ?? root?.data?.number ?? 0
  );
  const totalPages = root?.totalPages ?? root?.data?.totalPages;
  const last = root?.last ?? root?.data?.last;
  const hasMore =
    last === false ||
    (Number.isFinite(Number(totalPages)) && pageNumber + 1 < Number(totalPages)) ||
    content.length >= EMPLOYEE_BY_ROLE_PAGE_SIZE;
  return { content, page: Number.isFinite(pageNumber) ? pageNumber : 0, hasMore };
}

function mapEmploymentEmployeeOption(item) {
  if (!item || typeof item !== "object") return null;
  const id = item.empId ?? item.employeeId ?? item.emp_Id ?? item.id;
  if (id == null || String(id).trim() === "") return null;
  const first = item.firstName ?? item.first_name ?? "";
  const last = item.lastName ?? item.last_name ?? "";
  const fullName = `${first} ${last}`.trim();
  const name =
    item.name ||
    item.employeeName ||
    item.empName ||
    item.fullName ||
    fullName ||
    String(id);
  return {
    value: String(id),
    label: String(name).trim() || String(id),
    id: Number(id),
    name: String(name).trim() || String(id)
  };
}

function mergeEmployeeOptions(current = [], next = []) {
  const map = new Map();
  [...current, ...next].forEach((option) => {
    if (!option?.value) return;
    map.set(String(option.value), option);
  });
  return Array.from(map.values());
}

function ensureSelectedEmployeeOption(options, id, name) {
  const value = String(id || "").trim();
  if (!value) return options;
  const label = String(name || "").trim() || value;
  const existing = options.find((option) => option.value === value);
  if (existing) {
    if (!String(name || "").trim() || existing.label === label) return options;
    return options.map((option) => (
      option.value === value
        ? { ...option, label, name: label }
        : option
    ));
  }
  return mergeEmployeeOptions([{ value, label, id: Number(value), name: label }], options);
}

function ensureSelectedLookupOption(options, id, label) {
  const value = String(id || "").trim();
  if (!value) return options;
  if (options.some((option) => option.value === value)) return options;
  return [...options, { value, label: String(label || "").trim() || value }];
}

function LookupSelectField({
  id,
  label,
  value,
  options,
  loading,
  error,
  onValueChange,
  loadingPlaceholder,
  selectPlaceholder,
  emptyPlaceholder,
  enterPlaceholder,
  onChangeFallback,
  onBlurFallback
}) {
  if (options.length > 0) {
    return (
      <div className="phs-field">
        <label className="phs-label required" htmlFor={id}>{label}</label>
        <Select
          value={value || undefined}
          onValueChange={onValueChange}
          disabled={loading && options.length === 0}
        >
          <SelectTrigger id={id} className={`phs-select${error ? " error" : ""}`}>
            <SelectValue
              placeholder={
                loading
                  ? loadingPlaceholder
                  : options.length
                    ? selectPlaceholder
                    : emptyPlaceholder
              }
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="phs-error">{error || ""}</p>
      </div>
    );
  }

  return (
    <div className="phs-field">
      <label className="phs-label required" htmlFor={id}>{label}</label>
      <input
        id={id}
        className={`phs-input${error ? " error" : ""}`}
        name={id}
        placeholder={enterPlaceholder}
        value={value}
        onChange={onChangeFallback}
        onBlur={onBlurFallback}
      />
      <p className="phs-error">{error || ""}</p>
    </div>
  );
}

export default function PositionHistory({
  accessToken,
  tenantCode,
  initialEmployeeId = "",
  breadcrumb,
  onBack,
  onNext,
  onSuccess,
  isTabMode = false,
  isActive = true,
  saveTriggerRef
}) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });

  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    empId: String(initialEmployeeId || "")
  }));
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [errors, setErrors] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  /** Original effective date from backend — used as the update path key. */
  const [originalEffectiveDate, setOriginalEffectiveDate] = useState("");

  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [designationsLoading, setDesignationsLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [gradeLevelsLoading, setGradeLevelsLoading] = useState(false);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [costCentersLoading, setCostCentersLoading] = useState(false);
  const [workLocationOptions, setWorkLocationOptions] = useState([]);
  const [workLocationsLoading, setWorkLocationsLoading] = useState(false);
  const [empTypeOptions, setEmpTypeOptions] = useState([]);
  const [empTypesLoading, setEmpTypesLoading] = useState(false);
  const [managerOptions, setManagerOptions] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managersLoadingMore, setManagersLoadingMore] = useState(false);
  const [managerPage, setManagerPage] = useState(0);
  const [managerHasMore, setManagerHasMore] = useState(false);

  const managerFetchLockRef = useRef(false);
  const selectedManagerRef = useRef({ id: "", name: "" });
  const managersLoadedRef = useRef(false);
  const lookupsLoadedRef = useRef({
    roles: false,
    designations: false,
    departments: false,
    gradeLevels: false,
    divisions: false,
    costCenters: false,
    workLocations: false,
    empTypes: false
  });

  const sessionAccessToken = sessionDB.getAccessToken() || accessToken || "";
  const tenantId = sessionDB.getTenantId() || getAuthenticatedTenantId() || "";
  const tenantCodeFromSession = sessionDB.getTenantCode() || tenantCode || "";

  selectedManagerRef.current = { id: form.managerId, name: form.managerName };

  const fetchPosition = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const headers = {
        "x-tenant-code": tenantCodeFromSession || "",
        "x-tenant-id": String(tenantId || ""),
        "x-service-code": SEARCH_SERVICE_CODE,
        authorization: `Bearer ${sessionAccessToken}`,
        Accept: "application/json"
      };

      const response = await fetch(
        resolveApiUrl(`/saas/hrms/position-history/search/${Number(initialEmployeeId)}`),
        { method: "GET", headers }
      );

      if (!response.ok) return;

      const body = await response.json();
      const data = body.data || body;
      const candidates = [data?.content, data?.records, data?.items, data?.data, data];
      const arrayCandidate = candidates.find((c) => Array.isArray(c));
      const record = arrayCandidate ?
      arrayCandidate[0] :
      data && typeof data === "object" && !Array.isArray(data) ?
      data :
      null;

      if (!record || typeof record !== "object") return;

      const effectiveInputDate = toInputDate(record.effectiveDate);
      setForm({
        empId: String(initialEmployeeId),
        effectiveDate: effectiveInputDate,
        empType: record.empType || "",
        roleId: String(record.roleId ?? record.role_id ?? ""),
        designationId: String(record.designationId ?? record.designation_id ?? ""),
        departmentId: String(record.departmentId ?? record.department_id ?? ""),
        gradeLevelId: String(record.gradeLevelId ?? record.grade_level_id ?? ""),
        divisionId: String(record.divisionId ?? record.division_id ?? ""),
        costCenterId: String(record.costCenterId ?? record.cost_center_id ?? ""),
        workLocationId: String(record.workLocationId ?? record.work_location_id ?? record.locationId ?? ""),
        managerId: String(record.managerId ?? record.manager_id ?? ""),
        managerName: String(
          record.managerName ||
          record.manager_name ||
          record.managerFullName ||
          record.managerEmpName ||
          ""
        )
      });
      setOriginalEffectiveDate(toApiEffectiveDate(record.effectiveDate) || toApiEffectiveDate(effectiveInputDate));
      setIsUpdate(true);
    } catch (err) {
      console.error("Failed to load position history info", err);
    }
  }, [initialEmployeeId, tenantCodeFromSession, tenantId, sessionAccessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm({
        ...INITIAL_FORM,
        empId: ""
      });
      setIsUpdate(false);
      setOriginalEffectiveDate("");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
    fetchPosition();
  }, [isActive, initialEmployeeId, fetchPosition]);

  async function fetchOrgLookup(path) {
    if (!tenantId) {
      throw new Error("tenant missing");
    }

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCodeFromSession || "").trim();
    const nextAccessToken = String(sessionAccessToken || "").trim();
    const requestPath = `${path}${path.includes("?") ? "&" : "?"}tenantId=${encodeURIComponent(nextTenantId)}`;
    const headers = {
      Accept: "*/*",
      "x-service-code": "HR20A",
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {})
    };

    const response = await fetch(resolveOrgApiUrl(requestPath), {
      method: "GET",
      headers
    });

    if (!response.ok) {
      throw new Error(String(response.status || "request failed"));
    }

    return await response.json().catch(() => []);
  }

  async function fetchOrgLookupById(pathTemplate, idValue) {
    if (!tenantId) return null;

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCodeFromSession || "").trim();
    const nextAccessToken = String(sessionAccessToken || "").trim();
    const requestPath = `${pathTemplate}/${encodeURIComponent(String(idValue))}?tenantId=${encodeURIComponent(nextTenantId)}`;
    const headers = {
      Accept: "*/*",
      "x-service-code": "HR20A",
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {})
    };

    try {
      const response = await fetch(resolveOrgApiUrl(requestPath), {
        method: "GET",
        headers
      });
      if (!response.ok) return null;
      return await response.json().catch(() => ({}));
    } catch {
      return null;
    }
  }

  const fetchEmployeesByCanonicalRole = useCallback(async (searchTerm, page = 0) => {
    const params = new URLSearchParams({
      searchTerm: String(searchTerm || "").trim(),
      page: String(page),
      size: String(EMPLOYEE_BY_ROLE_PAGE_SIZE),
      sortBy: "first_name",
      sortDir: "ASC"
    });
    const response = await fetch(
      resolveApiUrl(`${EMPLOYEE_BY_CANONICAL_ROLE_ENDPOINT}?${params.toString()}`),
      {
        method: "GET",
        headers: {
          Accept: "*/*",
          "x-tenant-code": tenantCodeFromSession || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": "HR02S",
          authorization: `Bearer ${sessionAccessToken}`
        }
      }
    );
    if (!response.ok) {
      throw new Error(String(response.status || "request failed"));
    }
    const body = await response.json().catch(() => ({}));
    const pageData = unwrapEmployeeRolePage(body);
    return {
      ...pageData,
      content: pageData.content.map(mapEmploymentEmployeeOption).filter(Boolean)
    };
  }, [sessionAccessToken, tenantCodeFromSession, tenantId]);

  const loadManagerOptions = useCallback(async (page = 0, { append = false } = {}) => {
    if (!tenantId || managerFetchLockRef.current) return;
    managerFetchLockRef.current = true;
    if (append) setManagersLoadingMore(true);
    else setManagersLoading(true);
    try {
      const result = await fetchEmployeesByCanonicalRole("Manager", page);
      const selected = selectedManagerRef.current;
      setManagerOptions((prev) => {
        const merged = append
          ? mergeEmployeeOptions(prev, result.content)
          : mergeEmployeeOptions(result.content, prev.filter((option) => option.value === String(selected.id)));
        return ensureSelectedEmployeeOption(merged, selected.id, selected.name);
      });
      setManagerPage(result.page ?? page);
      setManagerHasMore(Boolean(result.hasMore));
    } catch {
      if (!append) {
        const selected = selectedManagerRef.current;
        setManagerOptions((prev) => ensureSelectedEmployeeOption(prev, selected.id, selected.name));
        setManagerHasMore(false);
      }
    } finally {
      managersLoadedRef.current = true;
      setManagersLoading(false);
      setManagersLoadingMore(false);
      managerFetchLockRef.current = false;
    }
  }, [fetchEmployeesByCanonicalRole, tenantId]);

  useEffect(() => {
    if (!isActive || !tenantId) return;

    let cancelled = false;
    const loaded = lookupsLoadedRef.current;

    async function loadLookup({
      key,
      endpoint,
      setOptions,
      setLoading,
      mapOptions,
      onError,
      cacheOnError = false
    }) {
      if (loaded[key]) return;
      loaded[key] = "loading";
      setLoading(true);
      try {
        const data = await fetchOrgLookup(endpoint);
        if (cancelled) {
          loaded[key] = false;
          return;
        }
        setOptions(mapOptions(data));
        loaded[key] = true;
      } catch (error) {
        if (cancelled) {
          loaded[key] = false;
          return;
        }
        if (onError) onError(error);
        else setOptions([]);
        loaded[key] = cacheOnError ? true : false;
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    Promise.all([
      loadLookup({
        key: "roles",
        endpoint: ROLES_ENDPOINT,
        setOptions: setRoleOptions,
        setLoading: setRolesLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["roleId", "id", "value"], ["roleName", "name", "label", "value"]),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadRoles")
        })
      }),
      loadLookup({
        key: "designations",
        endpoint: DESIGNATIONS_ENDPOINT,
        setOptions: setDesignationOptions,
        setLoading: setDesignationsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["designationId", "id", "value"], ["designationName", "name", "label", "value"]),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadDesignations")
        })
      }),
      loadLookup({
        key: "departments",
        endpoint: DEPARTMENTS_ENDPOINT,
        setOptions: setDepartmentOptions,
        setLoading: setDepartmentsLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["departmentId", "deptId", "id", "value"],
          ["departmentName", "deptName", "name", "label", "value"]
        ),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadDepartments")
        })
      }),
      loadLookup({
        key: "gradeLevels",
        endpoint: GRADE_LEVELS_ENDPOINT,
        setOptions: setGradeLevelOptions,
        setLoading: setGradeLevelsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["gradeLevelId", "id", "value"], ["gradeLevelName", "name", "label", "value"]),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadGradeLevels")
        })
      }),
      loadLookup({
        key: "divisions",
        endpoint: DIVISIONS_ENDPOINT,
        setOptions: setDivisionOptions,
        setLoading: setDivisionsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["divisionId", "id", "value"], ["divisionName", "name", "label", "value"]),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadDivisions")
        })
      }),
      loadLookup({
        key: "costCenters",
        endpoint: COST_CENTERS_ENDPOINT,
        setOptions: setCostCenterOptions,
        setLoading: setCostCentersLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["costCenterId", "id", "value"], ["costCenterName", "name", "label", "value"]),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadCostCenters")
        })
      }),
      loadLookup({
        key: "workLocations",
        endpoint: WORK_LOCATIONS_ENDPOINT,
        setOptions: setWorkLocationOptions,
        setLoading: setWorkLocationsLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["workLocationId", "locationId", "id", "value"],
          ["workLocationName", "locationName", "name", "label", "value"]
        ),
        onError: (error) => setBanner((current) => current || {
          type: "error",
          msg: error.message || i18n.t("onboarding.errors.unableToLoadWorkLocations")
        })
      }),
      loadLookup({
        key: "empTypes",
        endpoint: EMP_TYPES_ENDPOINT,
        setOptions: setEmpTypeOptions,
        setLoading: setEmpTypesLoading,
        mapOptions: (data) => {
          // Same as Current Employment: empType is stored by name (type_Name), not id.
          const options = normalizeEnumOptions(data, ["type_Name", "typeName"], ["type_Name", "typeName"]);
          return options.length
            ? translateEmpTypeOptions(options)
            : translateEmpTypeOptions(EMP_TYPES.map((value) => ({ value, label: value })));
        },
        onError: () => {
          setEmpTypeOptions(translateEmpTypeOptions(EMP_TYPES.map((value) => ({ value, label: value }))));
        },
        cacheOnError: true
      })
    ]);

    return () => {
      cancelled = true;
    };
  }, [isActive, tenantId, tenantCodeFromSession, sessionAccessToken]);

  useEffect(() => {
    if (!isActive || !tenantId) return;
    if (!managersLoadedRef.current) {
      loadManagerOptions(0);
    }
  }, [isActive, tenantId, loadManagerOptions]);

  useEffect(() => {
    setManagerOptions((prev) => ensureSelectedEmployeeOption(prev, form.managerId, form.managerName));
  }, [form.managerId, form.managerName]);

  useEffect(() => {
    if (!form.empType) return;
    setEmpTypeOptions((prev) => {
      if (prev.some((option) => option.value === form.empType)) return prev;
      return [...prev, { value: form.empType, label: translateEmpTypeLabel(form.empType) }];
    });
  }, [form.empType]);

  useEffect(() => {
    if (!form.managerId || form.managerName) return;
    const match = managerOptions.find((option) => option.value === String(form.managerId));
    if (!match?.name) return;
    setForm((prev) => (
      prev.managerId === match.value && !prev.managerName
        ? { ...prev, managerName: match.name }
        : prev
    ));
  }, [form.managerId, form.managerName, managerOptions]);

  async function hydrateLookupOption({
    id,
    options,
    setOptions,
    endpoint,
    labelKeys
  }) {
    if (!tenantId || !id || options.some((option) => option.value === String(id))) return;
    try {
      const data = await fetchOrgLookupById(endpoint, id);
      if (!data) {
        setOptions((current) => ensureSelectedLookupOption(current, id, id));
        return;
      }
      const label = extractLookupLabel(data, labelKeys);
      setOptions((current) => ensureSelectedLookupOption(current, id, label || id));
    } catch {
      setOptions((current) => ensureSelectedLookupOption(current, id, id));
    }
  }

  useEffect(() => {
    if (!isActive || !form.roleId) return;
    hydrateLookupOption({
      id: form.roleId,
      options: roleOptions,
      setOptions: setRoleOptions,
      endpoint: ROLE_BY_ID_ENDPOINT,
      labelKeys: ["roleName", "name", "label", "value"]
    });
  }, [isActive, form.roleId, roleOptions]);

  useEffect(() => {
    if (!isActive || !form.designationId) return;
    hydrateLookupOption({
      id: form.designationId,
      options: designationOptions,
      setOptions: setDesignationOptions,
      endpoint: DESIGNATION_BY_ID_ENDPOINT,
      labelKeys: ["designationName", "name", "label", "value"]
    });
  }, [isActive, form.designationId, designationOptions]);

  useEffect(() => {
    if (!isActive || !form.departmentId) return;
    hydrateLookupOption({
      id: form.departmentId,
      options: departmentOptions,
      setOptions: setDepartmentOptions,
      endpoint: DEPARTMENT_BY_ID_ENDPOINT,
      labelKeys: ["departmentName", "deptName", "name", "label", "value"]
    });
  }, [isActive, form.departmentId, departmentOptions]);

  useEffect(() => {
    if (!isActive || !form.gradeLevelId) return;
    hydrateLookupOption({
      id: form.gradeLevelId,
      options: gradeLevelOptions,
      setOptions: setGradeLevelOptions,
      endpoint: GRADE_LEVEL_BY_ID_ENDPOINT,
      labelKeys: ["gradeLevelName", "name", "label", "value"]
    });
  }, [isActive, form.gradeLevelId, gradeLevelOptions]);

  useEffect(() => {
    if (!isActive || !form.divisionId) return;
    hydrateLookupOption({
      id: form.divisionId,
      options: divisionOptions,
      setOptions: setDivisionOptions,
      endpoint: DIVISION_BY_ID_ENDPOINT,
      labelKeys: ["divisionName", "name", "label", "value"]
    });
  }, [isActive, form.divisionId, divisionOptions]);

  useEffect(() => {
    if (!isActive || !form.costCenterId) return;
    hydrateLookupOption({
      id: form.costCenterId,
      options: costCenterOptions,
      setOptions: setCostCenterOptions,
      endpoint: COST_CENTER_BY_ID_ENDPOINT,
      labelKeys: ["costCenterName", "name", "label", "value"]
    });
  }, [isActive, form.costCenterId, costCenterOptions]);

  useEffect(() => {
    if (!isActive || !form.workLocationId) return;
    hydrateLookupOption({
      id: form.workLocationId,
      options: workLocationOptions,
      setOptions: setWorkLocationOptions,
      endpoint: WORK_LOCATION_BY_ID_ENDPOINT,
      labelKeys: ["workLocationName", "locationName", "name", "label", "value"]
    });
  }, [isActive, form.workLocationId, workLocationOptions]);

  function handleManagerScroll(event) {
    if (!managerHasMore || managersLoadingMore || managersLoading) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadManagerOptions(managerPage + 1, { append: true });
    }
  }

  function validateForm(nextForm = form) {
    const nextErrors = {};

    if (!String(nextForm.empId || "").trim()) {
      nextErrors.empId = i18n.t("onboarding.errors.empIdRequired");
    } else if (!isNumericValue(nextForm.empId)) {
      nextErrors.empId = i18n.t("onboarding.errors.empIdMustBeNumeric");
    }

    if (!nextForm.effectiveDate) {
      nextErrors.effectiveDate = i18n.t("onboarding.errors.effectiveDateRequired");
    } else if (!isValidIsoDate(nextForm.effectiveDate)) {
      nextErrors.effectiveDate = i18n.t("onboarding.errors.useValidDate");
    }

    if (!nextForm.empType) {
      nextErrors.empType = i18n.t("onboarding.errors.employeeTypeRequired");
    } else if (
      empTypeOptions.length > 0 &&
      !empTypeOptions.some((option) => option.value === nextForm.empType)
    ) {
      nextErrors.empType = i18n.t("onboarding.errors.chooseValidEmployeeType");
    }

    [
      "roleId",
      "designationId",
      "departmentId",
      "gradeLevelId",
      "divisionId",
      "costCenterId",
      "workLocationId",
      "managerId"
    ].forEach((field) => {
      const label = getFieldLabel(field);
      if (!String(nextForm[field] || "").trim()) {
        nextErrors[field] = i18n.t("validation.fieldRequired", { field: label });
      } else if (!isNumericValue(nextForm[field])) {
        nextErrors[field] = i18n.t("onboarding.errors.fieldMustBeNumeric", { label });
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  function handleSelectChange(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  async function handleSave() {
    setBanner(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const empId = Number(String(form.empId).trim());
      const effectiveDateForPath = isUpdate ?
      originalEffectiveDate || toApiEffectiveDate(form.effectiveDate) :
      toApiEffectiveDate(form.effectiveDate);

      if (isUpdate && !effectiveDateForPath) {
        throw new Error(i18n.t("onboarding.errors.effectiveDateRequiredForUpdate"));
      }

      // Build URLs explicitly — do not rely on feature-config templates that omit effectiveDate.
      const addConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Position History"],
        action: "add",
        fallback: {
          title: "Add Position History",
          serviceCode: ADD_SERVICE_CODE,
          endpoint: ADD_ENDPOINT,
          method: "POST"
        }
      });
      const updateConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Position History"],
        action: "update",
        fallback: {
          title: "Update Position History",
          serviceCode: UPDATE_SERVICE_CODE,
          endpoint: `/saas/hrms/position-history/update/{empId}/{effectiveDate}`,
          method: "PUT"
        }
      });

      const endpoint = isUpdate ?
      `/saas/hrms/position-history/update/${empId}/${encodeURIComponent(effectiveDateForPath)}` :
      ADD_ENDPOINT;
      const method = isUpdate ? "PUT" : "POST";
      const serviceCode = isUpdate ?
      updateConfig.serviceCode || UPDATE_SERVICE_CODE :
      addConfig.serviceCode || ADD_SERVICE_CODE;

      if (!(isUpdate ? updateConfig.isAvailable : addConfig.isAvailable)) {
        throw new Error(
          buildOnboardingAccessMessage(
            i18n.t("onboarding.positionHistory"),
            isUpdate ? i18n.t("common.update") : i18n.t("common.add")
          )
        );
      }

      const payload = {
        empId,
        effectiveDate: form.effectiveDate,
        empType: form.empType,
        roleId: Number(form.roleId.trim()),
        designationId: Number(form.designationId.trim()),
        departmentId: Number(form.departmentId.trim()),
        gradeLevelId: Number(form.gradeLevelId.trim()),
        divisionId: Number(form.divisionId.trim()),
        costCenterId: Number(form.costCenterId.trim()),
        workLocationId: Number(form.workLocationId.trim()),
        managerId: Number(form.managerId.trim())
      };

      const response = await fetch(resolveApiUrl(endpoint), {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCodeFromSession,
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response);
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setBanner });
        return;
      }

      setBanner({ type: "success", msg: i18n.t("onboarding.messages.positionHistorySavedSuccessfully") });
      setIsUpdate(true);
      setOriginalEffectiveDate(toApiEffectiveDate(form.effectiveDate));
      if (isUpdate || initialEmployeeId) {
        await fetchPosition();
      }
      if (onSuccess) onSuccess();
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setBanner });
    } finally {
      setLoading(false);
    }
  }

  function handleFinish() {
    if (onNext) {
      onNext();
    }
  }

  return (
    <div className={`phs-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="phs-main">
        {!isTabMode && breadcrumb &&
        <div className="phs-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="phs-bc-sep">/</span>}
                <button
              className={`phs-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>

                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <OnboardingPageTitle
          onBack={onBack}
          employeeId={initialEmployeeId}
          titleClassName="phs-page-title"
          subtitleClassName="phs-page-sub"
          title={<Trans i18nKey="onboarding.positionHistory" />}
          subtitle={<Trans i18nKey="onboarding.manageJobPositionAssignmentsOrganizationalChangesAndEf" />}
        />
        }

        <div className="phs-form-card">
          <div className="phs-section">
            <div className="phs-section-title"><Trans i18nKey="onboarding.assignmentDetails" /></div>
            <div className="phs-grid">
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="dateTime.effectiveDate" /></label>
                <input className={`phs-input${errors.effectiveDate ? " error" : ""}`} name="effectiveDate" type="date" value={form.effectiveDate} onChange={handleChange} />
                {/* <p className="phs-hint"><Trans i18nKey="dateTime.selectTheEffectiveDateForThisPosition" /></p> */}
                <p className="phs-error">{errors.effectiveDate || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required" htmlFor="empType"><Trans i18nKey="onboarding.employeeType" /></label>
                {empTypeOptions.length > 0 ? (
                  <Select
                    value={form.empType || undefined}
                    onValueChange={(value) => handleSelectChange("empType", value)}
                    disabled={empTypesLoading && empTypeOptions.length === 0}
                  >
                    <SelectTrigger id="empType" className={`phs-select${errors.empType ? " error" : ""}`}>
                      <SelectValue
                        placeholder={
                          empTypesLoading
                            ? i18n.t("onboarding.loadingTypes")
                            : i18n.t("onboarding.selectType")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {empTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="phs-select-wrap">
                    <FormSelect
                      className={`phs-select${errors.empType ? " error" : ""}`}
                      name="empType"
                      value={form.empType}
                      onChange={handleChange}
                      disabled={empTypesLoading}
                    >
                      <option value="">
                        {empTypesLoading
                          ? i18n.t("onboarding.loadingTypes")
                          : i18n.t("onboarding.selectCategory")}
                      </option>
                    </FormSelect>
                  </div>
                )}
                <p className="phs-error">{errors.empType || ""}</p>
              </div>

              <LookupSelectField
                id="roleId"
                label={getFieldLabel("roleId")}
                value={form.roleId}
                options={roleOptions}
                loading={rolesLoading}
                error={errors.roleId}
                onValueChange={(value) => handleSelectChange("roleId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingRoles")}
                selectPlaceholder={i18n.t("onboarding.selectRole", { defaultValue: "Select role" })}
                emptyPlaceholder={i18n.t("onboarding.noRolesAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterRoleId")}
                onChangeFallback={handleChange}
              />

              <LookupSelectField
                id="designationId"
                label={getFieldLabel("designationId")}
                value={form.designationId}
                options={designationOptions}
                loading={designationsLoading}
                error={errors.designationId}
                onValueChange={(value) => handleSelectChange("designationId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingDesignations")}
                selectPlaceholder={i18n.t("onboarding.selectDesignation")}
                emptyPlaceholder={i18n.t("onboarding.noDesignationsAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterDesignationId")}
                onChangeFallback={handleChange}
              />

              <LookupSelectField
                id="departmentId"
                label={getFieldLabel("departmentId")}
                value={form.departmentId}
                options={departmentOptions}
                loading={departmentsLoading}
                error={errors.departmentId}
                onValueChange={(value) => handleSelectChange("departmentId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingDepartments")}
                selectPlaceholder={i18n.t("onboarding.selectDepartment")}
                emptyPlaceholder={i18n.t("onboarding.noDepartmentsAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterDepartmentId")}
                onChangeFallback={handleChange}
              />

              <LookupSelectField
                id="gradeLevelId"
                label={getFieldLabel("gradeLevelId")}
                value={form.gradeLevelId}
                options={gradeLevelOptions}
                loading={gradeLevelsLoading}
                error={errors.gradeLevelId}
                onValueChange={(value) => handleSelectChange("gradeLevelId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingGradeLevels")}
                selectPlaceholder={i18n.t("onboarding.selectGradeLevel")}
                emptyPlaceholder={i18n.t("onboarding.noGradeLevelsAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterGradeLevelId")}
                onChangeFallback={handleChange}
              />

              <LookupSelectField
                id="divisionId"
                label={getFieldLabel("divisionId")}
                value={form.divisionId}
                options={divisionOptions}
                loading={divisionsLoading}
                error={errors.divisionId}
                onValueChange={(value) => handleSelectChange("divisionId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingDivisions")}
                selectPlaceholder={i18n.t("onboarding.selectDivision")}
                emptyPlaceholder={i18n.t("onboarding.noDivisionsAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterDivisionId")}
                onChangeFallback={handleChange}
              />

              <LookupSelectField
                id="costCenterId"
                label={getFieldLabel("costCenterId")}
                value={form.costCenterId}
                options={costCenterOptions}
                loading={costCentersLoading}
                error={errors.costCenterId}
                onValueChange={(value) => handleSelectChange("costCenterId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingCostCenters")}
                selectPlaceholder={i18n.t("onboarding.selectCostCenter")}
                emptyPlaceholder={i18n.t("onboarding.noCostCentersAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterCostCenterId")}
                onChangeFallback={handleChange}
              />

              <LookupSelectField
                id="workLocationId"
                label={getFieldLabel("workLocationId")}
                value={form.workLocationId}
                options={workLocationOptions}
                loading={workLocationsLoading}
                error={errors.workLocationId}
                onValueChange={(value) => handleSelectChange("workLocationId", value)}
                loadingPlaceholder={i18n.t("onboarding.loadingWorkLocations")}
                selectPlaceholder={i18n.t("onboarding.selectWorkLocation")}
                emptyPlaceholder={i18n.t("onboarding.noWorkLocationsAvailable")}
                enterPlaceholder={i18n.t("onboarding.enterWorkLocationId")}
                onChangeFallback={handleChange}
              />

              <div className="phs-field">
                <label className="phs-label required" htmlFor="managerId">{getFieldLabel("managerId")}</label>
                <Select
                  value={form.managerId || undefined}
                  onValueChange={(value) => {
                    const option = managerOptions.find((entry) => entry.value === value);
                    setForm((prev) => ({
                      ...prev,
                      managerId: value,
                      managerName: option?.name || option?.label || ""
                    }));
                    if (errors.managerId) {
                      setErrors((prev) => ({ ...prev, managerId: "" }));
                    }
                  }}
                  disabled={managersLoading && managerOptions.length === 0}
                >
                  <SelectTrigger id="managerId" className={`phs-select${errors.managerId ? " error" : ""}`}>
                    <SelectValue
                      placeholder={
                        managersLoading
                          ? i18n.t("common.loading")
                          : i18n.t("formActions.searchManager")
                      }
                    >
                      {managerOptions.find((option) => option.value === form.managerId)?.label
                        || form.managerName
                        || undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent
                    searchPlaceholder={i18n.t("formActions.searchManager")}
                    viewportClassName="h-auto max-h-60 overflow-y-auto"
                    onViewportScroll={handleManagerScroll}
                  >
                    {managerOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value} textValue={option.label}>
                        {option.label}
                      </SelectItem>
                    ))}
                    {managersLoadingMore ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                        {i18n.t("common.loading")}
                      </div>
                    ) : null}
                  </SelectContent>
                </Select>
                <p className="phs-error">{errors.managerId || ""}</p>
              </div>
            </div>
          </div>
          {(onBack || onNext || banner) &&
          <div className="phs-section">
              {banner &&
            <div className={`phs-banner ${banner.type}`} role="alert">
                  {banner.msg}
                </div>
            }
              {!isTabMode &&
            <div className="phs-actions">
                  <button className="phs-primary-btn" type="button" onClick={handleSave} disabled={loading}>
                    {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                  </button>
                  {onNext ? <button className="phs-secondary-btn" type="button" onClick={handleFinish}><Trans i18nKey="onboarding.finish" /></button> : null}
                </div>
            }
            </div>
          }
        </div>
      </main>
    </div>);

}
